import { Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { financedEmissions } from "../../i18n";
import { DqsLevel } from "../../models/dto/cat15/cat15.types";

interface Props {
  /** DQS 1 (best) – 5 (worst), or "excluded" */
  level: DqsLevel | "excluded";
  size?: "small" | "medium";
}

/**
 * Solid-fill DQS badge — Figma SME-D-Bank (e.g. node 6091:51006): a round chip
 * showing just the level number in white. DQS 2 = #ffb200 is pixel-confirmed
 * from the borrower loans/emissions tables; the other levels follow the app's
 * DQS semantic (1 = best/green → 5 = worst/red) since those levels weren't in
 * the provided frames — refine against the DQS component node if it differs.
 */
const bgByLevel: Record<string, string> = {
  "1": "var(--status)", // #009864 green — best
  "2": "var(--yellow)", // #ffb200 — confirmed
  "3": "var(--status-pending-text)", // #e39e00 amber-dark
  "4": "var(--amber-dark)", // #ad7a00 — estimate from revenue
  "5": "var(--red)", // #ff4747 — estimate from outstanding balance
  excluded: "var(--gray1)", // #818181
};

const ESTIMATED_LEVELS = new Set(["4", "5"]);

const DqsBadge = ({ level, size = "small" }: Props) => {
  const { t } = useTranslation(financedEmissions);
  const bg = bgByLevel[String(level)] ?? bgByLevel.excluded;
  const diameter = size === "small" ? 24 : 28;
  const label = level === "excluded" ? "–" : String(level);
  const badge = (
    <Box
      component="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={diameter}
      height={diameter}
      borderRadius="50%"
      bgcolor={bg}
      color="var(--white)"
      sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 400 }}
    >
      {label}
    </Box>
  );
  if (!ESTIMATED_LEVELS.has(String(level))) return badge;

  // an estimate must never read as reported data, so the word travels with the number
  return (
    <Tooltip title={t(`DQS-Method-${level}`)} arrow>
      <Box component="span" display="inline-flex" alignItems="center" gap="6px">
        {badge}
        <Box component="span" sx={{ fontSize: "12px", color: "var(--gray9)" }}>
          {t("Estimated")}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default DqsBadge;
